/**
 * API Service
 * Handles funnel loading and lead submission
 */

import { createLogger } from '../core/logger';
import {
  getFunnelApiUrl,
  getLeadsApiUrl,
  getProgressApiUrl,
  getSessionsApiUrl,
  getFunnelName,
  isTestMode,
  isInProgressMode
} from '../core/config';
import {
  getDeviceInfo,
  DeviceInfo,
  getAllTrackingData,
  getFBP,
  getFBC,
  getConnectionInfo,
  ConnectionInfo,
  getBrowserFlags,
  BrowserFlags,
  getEngagementData,
  EngagementData
} from './tracking';
import { UTMParams } from '../types';
import {
  FunnelConfig,
  FunnelLoadResponse,
  Lead,
  LeadSubmitResponse,
  GraphicConfig
} from '../types';

const FUNNEL_DOMAIN = '1minutefunnel.com';

const logger = createLogger('API');

/**
 * Add cache-busting timestamp for test/in-progress modes
 */
function cacheBustUrl(url: string): string {
  if (isTestMode() || isInProgressMode()) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${Date.now()}`;
  }
  return url;
}

/**
 * Get the base URL for funnel API
 * Uses same pattern as original Player: /quizfunnels/{funnelName}/
 */
function getFunnelApiBase(): string {
  const funnelName = getFunnelName();
  // In production: use same-origin relative URLs
  // On localhost: use absolute URL to the funnel's subdomain
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

  if (isLocalhost) {
    return `https://${funnelName}.${FUNNEL_DOMAIN}/quizfunnels/${funnelName}`;
  }
  // Production: relative URL
  return `/quizfunnels/${funnelName}`;
}

/**
 * Resolve a potentially relative asset URL to absolute
 * Uses Worker API pattern: /quizfunnels/{funnelName}/asset/{path}
 */
function resolveAssetUrl(url: string | undefined | null): string | undefined {
  if (!url) return undefined;

  // Already absolute
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }

  // Relative path - resolve through Worker asset endpoint
  const apiBase = getFunnelApiBase();
  // Asset URL pattern: /quizfunnels/{funnelName}/asset/{assetPath}
  return `${apiBase}/asset/${url}`;
}

/**
 * Resolve GraphicConfig path
 */
function resolveGraphicConfig(config: string | GraphicConfig | undefined): string | GraphicConfig | undefined {
  if (!config) return undefined;

  if (typeof config === 'string') {
    return resolveAssetUrl(config);
  }

  // It's a GraphicConfig object
  return {
    ...config,
    path: resolveAssetUrl(config.path) || config.path
  };
}

/**
 * Transform funnel data to resolve all relative asset URLs
 */
function resolveAssetUrls(funnel: FunnelConfig): FunnelConfig {
  logger.debug('Resolving asset URLs', { apiBase: getFunnelApiBase() });

  return {
    ...funnel,
    // Resolve question images/videos
    questions: funnel.questions.map(q => ({
      ...q,
      image: resolveAssetUrl(q.image),
      video: resolveAssetUrl(q.video),
      options: q.options.map(opt => ({
        ...opt,
        image: resolveAssetUrl(opt.image)
      }))
    })),
    // Resolve final page image/graphic
    finalPage: {
      ...funnel.finalPage,
      image: resolveGraphicConfig(funnel.finalPage.image),
      graphic: resolveGraphicConfig(funnel.finalPage.graphic)
    },
    // Resolve logo - check both customLogo and logoSettings.path
    customLogo: resolveAssetUrl(funnel.customLogo || funnel.logoSettings?.path),
    // Preserve logoSettings for show/hide logic
    logoSettings: funnel.logoSettings ? {
      ...funnel.logoSettings,
      path: resolveAssetUrl(funnel.logoSettings.path)
    } : undefined
  };
}

// Declare global window properties set by inline script
declare global {
  interface Window {
    __funnelPromise?: Promise<FunnelConfig>;
    __funnelName?: string;
  }
}

/**
 * Load funnel configuration from API
 * Uses early-fetched promise from inline script if available
 */
export async function loadFunnel(): Promise<FunnelLoadResponse> {
  const funnelName = getFunnelName();

  if (!funnelName) {
    logger.error('No funnel name detected');
    return {
      success: false,
      error: {
        code: 'NO_FUNNEL',
        message: 'Could not determine which funnel to load'
      }
    };
  }

  // Check if we have an early-fetched promise from the inline script
  const earlyPromise = window.__funnelPromise;
  const earlyFunnelName = window.__funnelName;

  try {
    let data: FunnelConfig;

    if (earlyPromise && earlyFunnelName === funnelName) {
      // Use the early-fetched data (saves ~200-500ms on slow connections!)
      logger.info('Using early-fetched funnel data', { funnelName });
      data = await earlyPromise;
    } else {
      // Fallback: fetch normally
      const url = cacheBustUrl(getFunnelApiUrl());
      logger.info('Loading funnel (fallback)', { url, funnelName });
      const response = await fetch(url);

      if (!response.ok) {
        // Try to parse error body
        let errorData: Record<string, unknown> | null = null;
        try {
          errorData = await response.json();
        } catch {
          // Ignore JSON parse error
        }

        // Handle specific error codes
        if (response.status === 404) {
          const isDraftNotFound = errorData?.error === 'Draft Not Found';
          return {
            success: false,
            error: {
              code: isDraftNotFound ? 'DRAFT_NOT_FOUND' : 'NOT_FOUND',
              message: isDraftNotFound
                ? 'No draft version exists for this funnel.'
                : 'Funnel not found'
            }
          };
        }

        if (response.status === 403) {
          const isPaused = errorData?.status === 'paused';
          return {
            success: false,
            error: {
              code: isPaused ? 'PAUSED' : 'FORBIDDEN',
              message: isPaused
                ? 'This funnel is currently paused.'
                : 'Access denied',
              status: isPaused ? 'paused' : undefined
            }
          };
        }

        return {
          success: false,
          error: {
            code: 'HTTP_ERROR',
            message: `HTTP error: ${response.status}`
          }
        };
      }

      data = await response.json() as FunnelConfig;
    }

    logger.info('Funnel loaded successfully', {
      id: data.funnelID,
      version: data.version,
      questionCount: data.questions?.length
    });

    // Validate required fields
    if (!validateFunnel(data)) {
      return {
        success: false,
        error: {
          code: 'INVALID_CONFIG',
          message: 'Funnel configuration is invalid'
        }
      };
    }

    // Resolve relative asset URLs to absolute
    const processedFunnel = resolveAssetUrls(data);

    return {
      success: true,
      funnel: processedFunnel
    };
  } catch (error) {
    logger.error('Failed to load funnel', error);
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network error'
      }
    };
  }
}

/**
 * Validate funnel has required fields
 */
function validateFunnel(data: FunnelConfig): boolean {
  const required: (keyof FunnelConfig)[] = [
    'funnelID',
    'version',
    'title',
    'questions',
    'finalPage'
  ];

  for (const field of required) {
    if (!data[field]) {
      logger.error(`Funnel validation failed: missing '${field}'`);
      return false;
    }
  }

  if (!Array.isArray(data.questions) || data.questions.length === 0) {
    logger.error('Funnel validation failed: questions must be non-empty array');
    return false;
  }

  logger.debug('Funnel validation passed');
  return true;
}

/**
 * Submit lead to API
 */
export async function submitLead(leadData: Lead): Promise<LeadSubmitResponse> {
  const funnelName = getFunnelName();
  const url = getLeadsApiUrl();

  logger.info('Submitting lead', {
    leadID: leadData.leadID,
    funnelID: leadData.funnelID,
    testMode: leadData.testMode
  });

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        funnelName,
        leadData,
        previewMode: isInProgressMode() // In Progress mode = simulate, don't store
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const result = await response.json();

    if (result.preview) {
      logger.info('Lead simulated (In Progress mode)', { leadID: leadData.leadID });
    } else {
      logger.info('Lead saved successfully', { leadID: leadData.leadID });
    }

    return {
      success: true,
      leadID: leadData.leadID,
      result
    };
  } catch (error) {
    logger.error('Failed to submit lead', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to submit lead'
    };
  }
}

/**
 * Progress data for server-side saving
 */
export interface ServerProgressData {
  leadID: string;
  sessionId: string;
  funnelID: string;
  funnelVersion: number;
  currentQuestionIndex: number;
  totalQuestions: number;
  answers: Record<string, unknown>;
  userAgent: string;
  referrer: string | null;
  sourceUrl: string;
}

/**
 * Save progress to server (async, non-blocking)
 * This creates/updates an in-progress lead in R2 for drop-off tracking
 * Skip in In Progress mode (just previewing drafts)
 */
export async function saveProgressToServer(progressData: ServerProgressData): Promise<void> {
  // Skip in In Progress mode (previewing drafts)
  if (isInProgressMode()) {
    logger.debug('In Progress mode - skipping server progress save');
    return;
  }

  const funnelName = getFunnelName();
  const url = getProgressApiUrl();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        funnelName,
        progressData
      })
    });

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }

    const result = await response.json();
    logger.debug('Progress saved to server', { leadID: result.leadID, currentStep: result.currentStep });
  } catch (error) {
    // Fire and forget - don't let server errors break the quiz
    logger.warn('Failed to save progress to server', error);
  }
}

/**
 * Load text content (privacy policy, terms)
 */
export async function loadTextContent(url: string): Promise<{ success: boolean; data?: string; error?: string }> {
  try {
    const response = await fetch(cacheBustUrl(url));

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const text = await response.text();
    return { success: true, data: text };
  } catch (error) {
    logger.error('Failed to load text content', { url, error });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to load content'
    };
  }
}

/**
 * Session data for page view tracking
 */
export interface SessionData {
  sessionId: string;
  funnelID: string;
  funnelVersion: number;

  // Timing
  startedAt: number;
  lastActivityAt: number;
  quizStartedAt: number | null;
  quizCompletedAt: number | null;
  leadCreatedAt: number | null;

  // Performance (ms)
  pageLoadTime: number | null;
  funnelLoadTime: number | null;
  timeToInteractive: number | null;
  timeToFirstAnswer: number | null;

  // Outcome flags
  quizStarted: boolean;
  quizCompleted: boolean;
  leadCreated: boolean;

  // Error tracking
  loadError: boolean;
  errorCode: string | null;
  errorMessage: string | null;

  // Attribution
  utmParams: UTMParams;
  customParams: Record<string, string>;
  fbclid: string | null;
  gclid: string | null;
  fbp: string | null;
  fbc: string | null;

  // Context
  referrer: string;
  sourceUrl: string;
  device: DeviceInfo;
  connection: ConnectionInfo;
  browser: BrowserFlags;

  // Engagement (updated on session updates)
  engagement: EngagementData;

  // Geo (added server-side by Worker)
  geo?: {
    country: string | null;
    region: string | null;
    city: string | null;
    ipHash: string | null;
  };

  // Linked records
  leadId: string | null;
}

/**
 * Create a session on page load (tracks ad impressions/page views)
 * Skip in In Progress mode (just previewing drafts)
 */
export async function saveSession(sessionData: SessionData): Promise<{ success: boolean; sessionId?: string }> {
  // Skip in In Progress mode (previewing drafts)
  if (isInProgressMode()) {
    logger.debug('In Progress mode - skipping session save');
    return { success: true, sessionId: sessionData.sessionId };
  }

  const funnelName = getFunnelName();
  const url = getSessionsApiUrl();

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        funnelName,
        sessionData
      })
    });

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }

    const result = await response.json();
    logger.info('Session saved', { sessionId: result.sessionId });
    return { success: true, sessionId: result.sessionId };
  } catch (error) {
    // Fire and forget - don't let server errors break the page
    logger.warn('Failed to save session', error);
    return { success: false };
  }
}

/**
 * Get page load performance timing
 */
function getPageLoadTime(): number | null {
  if (typeof performance === 'undefined' || !performance.timing) {
    return null;
  }
  const timing = performance.timing;
  if (timing.loadEventEnd && timing.navigationStart) {
    return timing.loadEventEnd - timing.navigationStart;
  }
  // Fallback: use domContentLoadedEventEnd
  if (timing.domContentLoadedEventEnd && timing.navigationStart) {
    return timing.domContentLoadedEventEnd - timing.navigationStart;
  }
  return null;
}

/**
 * Create session data from current page state
 */
export function createSessionData(
  sessionId: string,
  funnelID: string,
  funnelVersion: number,
  funnelLoadTime: number | null = null,
  loadError: { code: string; message: string } | null = null
): SessionData {
  const trackingData = getAllTrackingData();
  const deviceInfo = getDeviceInfo();
  const connectionInfo = getConnectionInfo();
  const browserFlags = getBrowserFlags();
  const engagementData = getEngagementData();
  const now = Date.now();

  // Extract fbclid and gclid from custom params
  const fbclid = trackingData.customParams.fbclid || null;
  const gclid = trackingData.customParams.gclid || null;

  // Remove fbclid/gclid from customParams since they're tracked separately
  const { fbclid: _fb, gclid: _gc, ...otherCustomParams } = trackingData.customParams;

  return {
    sessionId,
    funnelID,
    funnelVersion,

    // Timing
    startedAt: now,
    lastActivityAt: now,
    quizStartedAt: null,
    quizCompletedAt: null,
    leadCreatedAt: null,

    // Performance
    pageLoadTime: getPageLoadTime(),
    funnelLoadTime,
    timeToInteractive: funnelLoadTime, // Quiz is interactive once funnel loads
    timeToFirstAnswer: null,

    // Outcome flags
    quizStarted: false,
    quizCompleted: false,
    leadCreated: false,

    // Error tracking
    loadError: loadError !== null,
    errorCode: loadError?.code || null,
    errorMessage: loadError?.message || null,

    // Attribution
    utmParams: trackingData.utmParams,
    customParams: otherCustomParams,
    fbclid,
    gclid,
    fbp: getFBP(),
    fbc: getFBC(),

    // Context
    referrer: trackingData.referrerInfo.referrer,
    sourceUrl: trackingData.referrerInfo.landingPage,
    device: deviceInfo,
    connection: connectionInfo,
    browser: browserFlags,

    // Engagement
    engagement: engagementData,

    // Linked records
    leadId: null
  };
}

/**
 * Session update types
 */
export type SessionUpdate = Partial<{
  lastActivityAt: number;
  quizStartedAt: number;
  quizCompletedAt: number;
  leadCreatedAt: number;
  timeToFirstAnswer: number;
  quizStarted: boolean;
  quizCompleted: boolean;
  leadCreated: boolean;
  leadId: string;
  questionsAnswered: number;
  engagement: EngagementData;
}>;

/**
 * Update session (called at various points in the quiz flow)
 */
export async function updateSession(
  funnelName: string,
  sessionId: string,
  updates: SessionUpdate
): Promise<void> {
  // Skip in In Progress mode
  if (isInProgressMode()) {
    return;
  }

  const url = `${getSessionsApiUrl()}/${funnelName}/${sessionId}`;

  try {
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        ...updates,
        lastActivityAt: Date.now()
      })
    });

    if (!response.ok) {
      throw new Error(`Server returned ${response.status}`);
    }

    logger.debug('Session updated', { sessionId, updates: Object.keys(updates) });
  } catch (error) {
    // Fire and forget
    logger.warn('Failed to update session', error);
  }
}

/**
 * Helper: Mark quiz started (called on first answer)
 */
export function updateSessionQuizStarted(
  funnelName: string,
  sessionId: string,
  leadId: string,
  startedAt: number
): void {
  const timeToFirstAnswer = Date.now() - startedAt;
  updateSession(funnelName, sessionId, {
    quizStarted: true,
    quizStartedAt: Date.now(),
    timeToFirstAnswer,
    leadId,
    engagement: getEngagementData()
  });
}

/**
 * Helper: Mark quiz completed
 */
export function updateSessionQuizCompleted(
  funnelName: string,
  sessionId: string
): void {
  updateSession(funnelName, sessionId, {
    quizCompleted: true,
    quizCompletedAt: Date.now(),
    engagement: getEngagementData()
  });
}

/**
 * Helper: Mark lead created
 */
export function updateSessionLeadCreated(
  funnelName: string,
  sessionId: string
): void {
  updateSession(funnelName, sessionId, {
    leadCreated: true,
    leadCreatedAt: Date.now(),
    engagement: getEngagementData()
  });
}
